import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import ChartCard from './ChartCard';
import ChartLegend, { LegendItem } from './ChartLegend';
import { RechartsTooltip } from './ChartTooltip';
import {
  BAR_MAX_SIZE,
  CHART_INK,
  CHART_SURFACE,
  STATUS_COLORS,
  WEATHER_COLORS,
  Y_AXIS_WIDTH,
} from './chartTheme';
import { formatBucketLabel, formatBucketTitle } from './formatters';
import { getWeekendLegendItem, renderWeekendLines } from './weekendLines';
import { useStatusLabels } from './useStatusLabels';
import {
  BucketWeather,
  emptyStatusCounts,
  STATUS_KEYS,
  StatusCounts,
  TimeBucket,
} from '../../utils/statistics';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

interface ReservationsChartProps {
  buckets: TimeBucket[];
  /** Weather per bucket key; temperature and rain are hidden without it. */
  weather?: Map<string, BucketWeather>;
}

const COUNT_AXIS = 'count';
const TEMPERATURE_AXIS = 'temperature';
const RAIN_AXIS = 'rain';

// Light rain should not look like a downpour
const MIN_RAIN_AXIS_MM = 10;

export const formatTemperature = (value: number) => `${value.toFixed(1)}°C`;
export const formatRain = (value: number) => `${value.toFixed(1)} mm`;

const ReservationsChart = ({ buckets, weather }: ReservationsChartProps) => {
  const { t, i18n } = useTranslation();

  const labels = useStatusLabels();

  const totals = useMemo(
    () =>
      buckets.reduce<StatusCounts>((acc, bucket) => {
        STATUS_KEYS.forEach((key) => {
          acc[key] += bucket[key];
        });

        return acc;
      }, emptyStatusCounts()),
    [buckets]
  );

  const data = useMemo(
    () =>
      buckets.map((b) => ({
        ...b,
        label: formatBucketLabel(dayjs(b.date), b.unit, buckets.length, i18n.language),
        title: formatBucketTitle(dayjs(b.date), b.unit, i18n.language),
        temperature: weather?.get(b.key)?.temperature ?? null,
        rain: weather?.get(b.key)?.rain ?? null,
      })),
    [buckets, weather, i18n.language]
  );

  const hasWeather = data.some(
    (point) => point.temperature !== null || point.rain !== null
  );

  const getWeatherRows = (point: (typeof data)[number]) =>
    hasWeather
      ? [
          {
            key: 'temperature',
            label: t(COMMON_TRANSLATE_KEYS.STATS_TEMPERATURE),
            color: WEATHER_COLORS.temperature,
            value:
              point.temperature === null
                ? '–'
                : formatTemperature(point.temperature),
          },
          {
            key: 'rain',
            label: t(COMMON_TRANSLATE_KEYS.STATS_RAIN),
            color: WEATHER_COLORS.rain,
            value: point.rain === null ? '–' : formatRain(point.rain),
          },
        ]
      : [];

  const weatherLegendItems: LegendItem[] = hasWeather
    ? [
        {
          key: 'temperature',
          label: `${t(COMMON_TRANSLATE_KEYS.STATS_TEMPERATURE)} (°C)`,
          color: WEATHER_COLORS.temperature,
          swatch: 'line',
        },
        {
          key: 'rain',
          label: `${t(COMMON_TRANSLATE_KEYS.STATS_RAIN)} (mm)`,
          color: WEATHER_COLORS.rain,
        },
      ]
    : [];

  return (
    <ChartCard
      title={t(COMMON_TRANSLATE_KEYS.STATS_RESERVATIONS_CHART)}
      subtitle={t(COMMON_TRANSLATE_KEYS.STATS_RESERVATIONS_CHART_HINT)}
      legend={
        <ChartLegend
          items={[
            ...STATUS_KEYS.map((key) => ({
              key,
              label: labels[key],
              color: STATUS_COLORS[key],
              value: String(totals[key]),
            })),
            ...weatherLegendItems,
            ...getWeekendLegendItem(
              buckets,
              t(COMMON_TRANSLATE_KEYS.STATS_WEEKEND)
            ),
          ]}
        />
      }
    >
      <div className="h-[320px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            margin={{ top: 8, right: hasWeather ? 0 : 8, left: 0, bottom: 0 }}
            // Fixed bar sizes keep the reservations and rain bars of a day
            // next to each other instead of spread across the day's slot
            barGap={2}
          >
            <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
            <XAxis
              dataKey="label"
              tick={{ fill: CHART_INK.muted, fontSize: 12 }}
              axisLine={{ stroke: CHART_INK.axis }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={8}
            />
            <YAxis
              yAxisId={COUNT_AXIS}
              width={Y_AXIS_WIDTH}
              allowDecimals={false}
              tick={{ fill: CHART_INK.muted, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            {hasWeather && (
              <YAxis
                yAxisId={TEMPERATURE_AXIS}
                orientation="right"
                width={Y_AXIS_WIDTH}
                tick={{ fill: CHART_INK.muted, fontSize: 12 }}
                tickFormatter={(value: number) => `${Math.round(value)}°`}
                axisLine={false}
                tickLine={false}
                domain={['auto', 'auto']}
              />
            )}
            {hasWeather && (
              <YAxis
                yAxisId={RAIN_AXIS}
                hide
                domain={[
                  0,
                  (dataMax: number) => Math.max(MIN_RAIN_AXIS_MM, dataMax),
                ]}
              />
            )}
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
              content={
                <RechartsTooltip
                  labels={labels}
                  getTitle={(item) => item.title}
                  getExtraRows={getWeatherRows}
                />
              }
            />
            {STATUS_KEYS.map((key, index) => (
              <Bar
                key={key}
                yAxisId={COUNT_AXIS}
                dataKey={key}
                stackId="status"
                fill={STATUS_COLORS[key]}
                stroke={CHART_SURFACE}
                strokeWidth={1}
                barSize={BAR_MAX_SIZE}
                radius={index === STATUS_KEYS.length - 1 ? [4, 4, 0, 0] : 0}
                isAnimationActive={false}
              />
            ))}
            {hasWeather && (
              <Bar
                yAxisId={RAIN_AXIS}
                dataKey="rain"
                fill={WEATHER_COLORS.rain}
                barSize={BAR_MAX_SIZE}
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              />
            )}
            {hasWeather && (
              <Line
                yAxisId={TEMPERATURE_AXIS}
                dataKey="temperature"
                stroke={WEATHER_COLORS.temperature}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={{
                  r: 4,
                  fill: WEATHER_COLORS.temperature,
                  stroke: CHART_SURFACE,
                  strokeWidth: 2,
                }}
                activeDot={{ r: 5, stroke: CHART_SURFACE, strokeWidth: 2 }}
                connectNulls
                isAnimationActive={false}
              />
            )}
            {renderWeekendLines(data, COUNT_AXIS)}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {hasWeather && (
        <p className="text-xxs text-textGray text-right">
          <a
            href="https://www.visualcrossing.com/"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            {t(COMMON_TRANSLATE_KEYS.STATS_WEATHER_SOURCE)}
          </a>
        </p>
      )}
    </ChartCard>
  );
};

export default ReservationsChart;
